import { encounterField } from '../../shared/array-types.js';
import { record, upper, type Row } from '../ability/context.js';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers.js';
import { normalizeInventoryGeneration } from '../compile/inventory-generation.js';
import { climbDenialReason } from './climb-walk.js';
import { compoundActorIds } from './compound-drag.js';
import type { PlainWalkCommitInput } from './plain-walk.js';
import type { PlainWalkReaders } from './plain-walk-scope.js';
import { sceneWallClimbGeometry, wallClimbGeometry, type WallClimbGeometry } from './wall-climb-geometry.js';

/** Re-author a radial climb from the parent's held rows and the scene's live architecture. */
export function prepareWallClimbRoute(input: PlainWalkCommitInput, encounter: Row, mover: Row, read: PlainWalkReaders): WallClimbGeometry {
  const request = input.wallClimb!;
  const no = (reason: string): WallClimbGeometry => ({ ok: false, refusal: reason });
  const body = read.geometry(mover);
  if (!body) return no('movement:not-ported:wall-climb:mover-geometry');
  const denial = climbDenialReason(mover, { ...read, value: (row, key) => row ? read.value(row, key) : undefined,
    entries: (row, key) => row ? read.entries(row, key) : [] });
  if (denial) return no(denial);
  if (compoundActorIds(encounter, mover, read).size > 1) return no('Dismount or release the carried creature before climbing.');
  let shape = input.shape ?? encounterField(encounter, 'encounter_shape');
  if (typeof shape === 'string') { try { shape = JSON.parse(shape) as unknown; } catch { return no('movement:not-ported:wall-climb:shape'); } }
  const architecture = record(shape);
  if (!architecture || !Array.isArray(architecture.boxes)) return no('movement:not-ported:wall-climb:shape');
  let id = request.id;
  let generation: Row | null = null;
  if (!request.sceneWall) {
    const row = readInventoryRows(encounter, 'encounter_inventories_arr').find(row => upper(readInventoryField(row, 'inventory_id')) === upper(id));
    if (!row || Number(readInventoryField(row, 'inventory_deleted') ?? 0) === 1) return no('That is not a wall you can climb.');
    generation = normalizeInventoryGeneration(readInventoryField(row, 'inventory_generation'));
    if (!generation?.wallRun) return no('That is not a wall you can climb.');
    const sceneId = record(generation.wallRun)?.sceneWallId;
    if (typeof sceneId === 'string') id = sceneId;
  }
  const origin = read.position(mover), radius = body.diameter / 2, size = body.rulesDiameter ?? body.diameter;
  const run = record(generation?.wallRun);
  const scene = request.sceneWall || typeof run?.sceneWallId === 'string';
  const wall = scene && Array.isArray(architecture.sceneWalls)
    ? architecture.sceneWalls.map(record).find(wall => wall?.id === id) : null;
  if (scene && !wall) return no('That wall cannot be climbed.');
  const result = scene ? sceneWallClimbGeometry(wall!, origin, radius, size, request.direction, request.holdFt ?? null, request.side ?? 0)
    : wallClimbGeometry(architecture as Parameters<typeof wallClimbGeometry>[0], run!, String(generation?.roomId ?? ''),
      origin, radius, size, request.direction, request.holdFt ?? null);
  if (result.ok) result.plan.wallRunId = id;
  return result;
}
